/**
 * Supabase Getter Layer
 *
 * Centralized query executor with integrated caching and deduplication.
 * Provides entity-specific getters for all database tables.
 *
 * Cache Key Strategy:
 * - Parameterized methods use explicit cache keys: {table}:{method}:{params}
 * - Arrays are normalized (sorted) for consistent cache hits
 * - See cache-manager.generateCacheKey() for implementation details
 */

import { supabase } from '~/supabase-client';
import { cacheManager } from './cache-manager';
import { queryDeduplicationManager } from './query-deduplication-manager';

class SupabaseGetter {
  /**
   * Core query method with caching and deduplication
   * @param {string} table - Table name
   * @param {Promise} queryPromise - Promise from a Supabase query builder
   * @param {Object} options - Query options
   * @param {boolean} options.skipCache - Skip cache lookup
   * @param {string} options.cacheKey - Custom cache key (auto-generated if not provided)
   * @param {number} options.ttl - Custom TTL in milliseconds
   * @returns {Promise<{data, error}>}
   */
  async query(table, queryPromise, options = {}) {
    const { skipCache = false, cacheKey = null, ttl = null } = options;

    // Generate cache key from table and provided cacheKey
    const key = cacheKey || cacheManager.generateKey(table, {
      query: 'default'
    });

    // Check cache first (unless skipCache is true)
    if (!skipCache) {
      const cached = cacheManager.get(key, table);
      if (cached) {
        console.log(`[CACHE HIT] ${table}`);
        return cached;
      }
    }

    console.log(`[CACHE MISS] ${table}`);

    // Use deduplication to prevent multiple identical requests
    const result = await queryDeduplicationManager.getOrCreate(key, async () => {
      try {
        console.log(`[SUPABASE GETTER] Executing query for ${table} with key: ${key}`);
        // Execute the query promise (no callback context issues)
        const { data, error } = await queryPromise;
        console.log(`[SUPABASE GETTER] Query complete for ${table}, error:`, error ? error.message : 'none');

        if (error) {
          console.error(`[SUPABASE ERROR] ${table}:`, error);
          return { data: null, error };
        }

        // Store successful result in cache
        const resultToCache = { data, error: null };
        cacheManager.set(key, resultToCache, ttl);

        return resultToCache;
      } catch (err) {
        console.error(`[QUERY ERROR] ${table}:`, err);
        return { data: null, error: err };
      }
    });

    return result;
  }

  /**
   * Users entity getters
   */
  users = {
    getById: async (id) => {
      const query = supabase.from('users').select('*').eq('id', id).single();
      return this.query('users', query, {
        cacheKey: cacheManager.generateCacheKey('users', 'getById', id)
      });
    },

    getByEmail: async (email) => {
      const query = supabase.from('users').select('*').eq('email', email).single();
      return this.query('users', query, {
        cacheKey: cacheManager.generateCacheKey('users', 'getByEmail', email)
      });
    },

    invalidate: () => cacheManager.invalidate('users')
  };

  userPreferenceTypes = {
    getAll: async () => {
      const query = supabase.from('user_preference_types').select('*');
      return this.query('user_preference_types', query);
    },

    getByCode: async (code) => {
      const query = supabase.from('user_preference_types').select('*').eq('code', code).single();
      return this.query('user_preference_types', query, {
        cacheKey: cacheManager.generateCacheKey('user_preference_types', 'getByCode', code)
      });
    },

    invalidate: () => cacheManager.invalidate('user_preference_types')
  }

  /**
   * Organizations entity getters
   */
  organizations = {
    getAll: async () => {
      const query = supabase.from('organizations').select('*');
      return this.query('organizations', query);
    },

    getById: async (id) => {
      const query = supabase.from('organizations').select('*').eq('id', id).single();
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getById', id)
      });
    },

    getByIds: async (ids) => {
      if (!ids || ids.length === 0) {
        return { data: [], error: null };
      }
      const query = supabase.from('organizations').select('*').in('id', ids);
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getByIds', ids)
      });
    },

    getByType: async (type) => {
      const query = supabase.from('organizations').select('*').eq('organization_type', type);
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getByType', type)
      });
    },

    getByParent: async (parentId) => {
      const query = supabase.from('organizations').select('*')
        .eq('parent_organization_id', parentId);
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getByParent', parentId)
      });
    },

    getByParentAndType: async (parentId, type) => {
      const query = supabase.from('organizations').select('*')
        .eq('parent_organization_id', parentId)
        .eq('organization_type', type);
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getByParentAndType', { parentId, type })
      });
    },

    getFarms: async () => {
      const query = supabase.from('organizations').select('*')
        .eq('organization_type', 'farm')
        .order('name', { ascending: false });
      return this.query('organizations', query);
    },

    getPlots: async (farmId) => {
      const query = supabase.from('organizations').select('*')
        .eq('parent_organization_id', farmId)
        .eq('organization_type', 'plot');
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getPlots', farmId)
      });
    },

    getPatches: async (plotId) => {
      const query = supabase.from('organizations').select('*')
        .eq('parent_organization_id', plotId)
        .eq('organization_type', 'patch');
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getPatches', plotId)
      });
    },

    getPatchesWithNames: async (plotId) => {
      const query = supabase.from('organizations').select('id, name')
        .eq('parent_organization_id', plotId)
        .eq('organization_type', 'patch');
      return this.query('organizations', query, {
        cacheKey: cacheManager.generateCacheKey('organizations', 'getPatchesWithNames', plotId)
      });
    },

    invalidate: () => cacheManager.invalidate('organizations')
  };

  /**
   * Organization Users entity getters
   */
  organizationUsers = {
    getByUser: async (userId) => {
      const query = supabase.from('organization_users').select('organization_id')
        .eq('user_id', userId);
      return this.query('organization_users', query, {
        cacheKey: cacheManager.generateCacheKey('organization_users', 'getByUser', userId)
      });
    },

    getByOrganization: async (orgId) => {
      const query = supabase.from('organization_users').select('*').eq('organization_id', orgId);
      return this.query('organization_users', query, {
        cacheKey: cacheManager.generateCacheKey('organization_users', 'getByOrganization', orgId)
      });
    },

    getByOrganizationWithUsers: async (orgId) => {
      const query = supabase
        .from('organization_users')
        .select('role, user_id, users(id, username, email, created_at, updated_at)')
        .eq('organization_id', orgId);
      return this.query('organization_users', query, {
        cacheKey: cacheManager.generateCacheKey('organization_users', 'getByOrganizationWithUsers', orgId)
      });
    },

    invalidate: () => cacheManager.invalidate('organization_users')
  };

  /**
   * Organization User Roles reference data
   */
  organizationUserRoles = {
    getAll: async () => {
      const query = supabase.from('organization_user_roles').select('*');
      return this.query('organization_user_roles', query, {
        cacheKey: cacheManager.generateCacheKey('organization_user_roles', 'getAll', null)
      });
    },

    invalidate: () => cacheManager.invalidate('organization_user_roles')
  };

  /**
   * Invite tickets getters
   */
  inviteTickets = {
    getById: async (ticketId) => {
      const query = supabase
        .from('invite_tickets')
        .select('*, invite_ticket_activations(id)')
        .eq('id', ticketId)
        .single();
      return this.query('invite_tickets', query, {
        cacheKey: cacheManager.generateCacheKey('invite_tickets', 'getById', ticketId),
        skipCache: true // always fresh for validation
      });
    },

    invalidate: () => cacheManager.invalidate('invite_tickets')
  };

  climates = {
    getAll: async () => {
      const query = supabase.from('organization_climates').select('*');
      return this.query('organization_climates', query);
    },

    getByOrganization: async (orgId) => {
      const query = supabase.from('organization_climates').select('*')
        .eq('organization_id', orgId)
        .maybeSingle();
      return this.query('organization_climates', query, {
        cacheKey: cacheManager.generateCacheKey('organization_climates', 'getByOrganization', orgId)
      });
    },

    invalidate: () => cacheManager.invalidate('organization_climates')
  };

  /**
   * Points entity getters
   */
  organizationPolygons = {
    getAll: async () => {
      const query = supabase.from('organization_polygons').select('*');
      return this.query('organization_polygons', query);
    },

    getByOrganization: async (orgId) => {
      const query = supabase.from('organization_polygons').select('*')
        .eq('organization_id', orgId)
        .single();
      return this.query('organization_polygons', query, {
        cacheKey: cacheManager.generateCacheKey('organization_polygons', 'getByOrganization', orgId)
      });
    },

    getByOrganizations: async (orgIds) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const query = supabase.from('organization_polygons').select('*')
        .in('organization_id', orgIds)
        .order('created_at', { ascending: false });
      return this.query('organization_polygons', query, {
        cacheKey: cacheManager.generateCacheKey('organization_polygons', 'getByOrganizations', orgIds)
      });
    },

    invalidate: () => cacheManager.invalidate('organization_polygons')
  };

  /**
   * Reference Polygons entity getters
   */
  referencePolygons = {
    getByFarm: async (farmId) => {
      const query = supabase.from('reference_polygons').select('*')
        .eq('parent_organization_id', farmId)
        .order('created_at', { ascending: false });
      return this.query('reference_polygons', query, {
        cacheKey: cacheManager.generateCacheKey('reference_polygons', 'getByFarm', farmId)
      });
    },

    invalidate: () => cacheManager.invalidate('reference_polygons')
  };

  /**
   * Seasons entity getters
   */
  seasons = {
    getByFarm: async (farmId) => {
      const query = supabase.from('seasons').select('*')
        .eq('organization_id', farmId)
        .order('year', { ascending: false });
      return this.query('seasons', query, {
        cacheKey: cacheManager.generateCacheKey('seasons', 'getByFarm', farmId)
      });
    },

    getActiveByFarm: async (farmId) => {
      const query = supabase.from('seasons').select('*')
        .eq('organization_id', farmId)
        .eq('state', 'active')
        .maybeSingle();
      return this.query('seasons', query, {
        cacheKey: cacheManager.generateCacheKey('seasons', 'getActiveByFarm', farmId)
      });
    },

    getById: async (id) => {
      const query = supabase.from('seasons').select('*').eq('id', id).single();
      return this.query('seasons', query, {
        cacheKey: cacheManager.generateCacheKey('seasons', 'getById', id)
      });
    },

    invalidate: () => cacheManager.invalidate('seasons')
  };

  /**
   * Crops entity getters
   *
   * Select fragments:
   * - CROP_SELECT_BASE: shared joins for all crop queries
   * - CROP_SCHEDULE_YIELD: embeds schedule yield for the active season (left join)
   * - CROP_SCHEDULE_SEASON_INNER: inner-join to filter crops by a specific season_id
   */
  #CROP_SELECT_BASE = `*, organization:organizations(id, name, organization_type), crop_details(template_id, family_code, archetype_code, growth_code, lifecycle_code, notes, crop_family_types(name))`
  #CROP_SCHEDULE_YIELD = `crop_schedules(yield, season_id, season:seasons(state))`
  #CROP_SCHEDULE_SEASON_INNER = `crop_schedules!inner(yield, season_id)`

  crops = {
    getAll: async () => {
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`);
      return this.query('crops', query);
    },

    getById: async (id) => {
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`)
        .eq('id', id)
        .single();
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getById', id)
      });
    },

    getByIds: async (ids) => {
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`)
        .in('id', ids);
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByIds', ids)
      });
    },

    getByOrganization: async (orgId) => {
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`)
        .eq('organization_id', orgId)
        .order('created_at', { ascending: true });
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByOrganization', orgId)
      });
    },

    getByOrganizations: async (orgIds) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`)
        .in('organization_id', orgIds)
        .order('created_at', { ascending: true });
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByOrganizations', orgIds)
      });
    },

    // Inner-join through crop_schedules so only crops with a schedule in this season are returned.
    // This replaces the old crops.season_id filter (crops no longer carry season_id).
    getByOrganizationAndSeason: async (orgId, seasonId) => {
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_SEASON_INNER}`)
        .eq('organization_id', orgId)
        .eq('crop_schedules.season_id', seasonId)
        .order('created_at', { ascending: true });
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByOrganizationAndSeason', { orgId, seasonId })
      });
    },

    getByOrganizationsAndSeason: async (orgIds, seasonId) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_SEASON_INNER}`)
        .in('organization_id', orgIds)
        .eq('crop_schedules.season_id', seasonId)
        .order('created_at', { ascending: true });
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByOrganizationsAndSeason', { orgIds, seasonId })
      });
    },

    getWithUserByOrganizationsAndSeason: async (orgIds, seasonId) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...orgIds].sort();
      const query = supabase.from('crops')
        .select(`
          id, name, created_at, organization_id,
          created_by:users!crops_created_by_fkey(id, username, email, avatar_url),
          organization:organizations(id, name),
          crop_details(notes),
          crop_schedules!inner(season_id)
        `)
        .in('organization_id', sortedIds)
        .eq('crop_schedules.season_id', seasonId);
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getWithUserByOrganizationsAndSeason', { sortedIds, seasonId })
      });
    },

    getByParentIds: async (parentIds) => {
      if (!parentIds || parentIds.length === 0) {
        return { data: [], error: null };
      }
      const query = supabase.from('crops')
        .select(`${this.#CROP_SELECT_BASE}, ${this.#CROP_SCHEDULE_YIELD}`)
        .in('parent_crop_id', parentIds);
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getByParentIds', parentIds)
      });
    },

    // Lightweight count of parent crops (successions excluded) across all orgs and all seasons.
    countUniqueByOrganizations: async (orgIds) => {
      if (!orgIds || orgIds.length === 0) return { data: 0, error: null };
      const sortedIds = [...orgIds].sort();
      const { count, error } = await supabase
        .from('crops')
        .select('id', { count: 'exact', head: true })
        .in('organization_id', sortedIds)
        .is('parent_crop_id', null);
      return { data: count ?? 0, error };
    },

    // Lightweight succession child lookup — returns only scheduling fields needed for
    // succession count seeding and succession management in the edit-crop flow.
    getSuccessionChildrenByParent: async (parentId) => {
      const query = supabase.from('crops')
        .select('id, succession, succession_interval_days')
        .eq('parent_crop_id', parentId)
        .order('succession', { ascending: true });
      return this.query('crops', query, {
        cacheKey: cacheManager.generateCacheKey('crops', 'getSuccessionChildrenByParent', parentId)
      });
    },

    invalidate: () => cacheManager.invalidate('crops')
  };

  /**
   * Crop Details entity getters (infrequently-accessed fields extracted from crops)
   */
  cropDetails = {
    getById: async (cropId) => {
      // Use maybeSingle so child/succession crops (no crop_details row) return null, not an error
      const query = supabase.from('crop_details').select('*').eq('crop_id', cropId).maybeSingle();
      return this.query('crop_details', query, {
        cacheKey: cacheManager.generateCacheKey('crop_details', 'getById', cropId)
      });
    },

    getByCropIds: async (cropIds) => {
      if (!cropIds || cropIds.length === 0) return { data: [], error: null };
      const sortedIds = [...cropIds].sort();
      const query = supabase.from('crop_details').select('*').in('crop_id', sortedIds);
      return this.query('crop_details', query, {
        cacheKey: cacheManager.generateCacheKey('crop_details', 'getByCropIds', sortedIds)
      });
    },

    invalidate: () => cacheManager.invalidate('crop_details')
  };

  /**
   * Crop Templates entity getters
   */
  cropTemplates = {
    getAll: async () => {
      const query = supabase.from('crop_templates').select('*');
      return this.query('crop_templates', query);
    },

    getById: async (id) => {
      const query = supabase.from('crop_templates').select('*').eq('id', id).single();
      return this.query('crop_templates', query, {
        cacheKey: cacheManager.generateCacheKey('crop_templates', 'getById', id)
      });
    },

    getByUser: async (userId) => {
      const query = supabase.from('crop_templates').select('*').eq('user_id', userId);
      return this.query('crop_templates', query, {
        cacheKey: cacheManager.generateCacheKey('crop_templates', 'getByUser', userId)
      });
    },

    getByArchetype: async (archetypeCode) => {
      const query = supabase.from('crop_templates').select('*').eq('archetype_code', archetypeCode);
      return this.query('crop_templates', query, {
        cacheKey: cacheManager.generateCacheKey('crop_templates', 'getByArchetype', archetypeCode)
      });
    },

    getByCollections: async (collectionCodes) => {
      const sortedCodes = [...collectionCodes].sort();
      const query = supabase.from('crop_templates').select('*').in('collection_code', sortedCodes);
      return this.query('crop_templates', query, {
        cacheKey: cacheManager.generateCacheKey('crop_templates', 'getByCollections', sortedCodes)
      });
    },

    getByParent: async (parentId) => {
      const query = supabase.from('crop_templates').select('*')
        .eq('parent_template_id', parentId)
        .order('name', { ascending: true });
      return this.query('crop_templates', query, {
        cacheKey: cacheManager.generateCacheKey('crop_templates', 'getByParent', parentId)
      });
    },

    invalidate: () => cacheManager.invalidate('crop_templates')
  };

  /**
   * Crop Template Propagation (M2M junction table)
   */
  cropTemplatePropagation = {
    /**
     * Get all propagation codes for a single template
     * @param {string} templateId - Template UUID
     * @returns {Promise<{data: Array<{propagation_code: string}>, error}>}
     */
    getByTemplate: async (templateId) => {
      const query = supabase
        .from('crop_template_propagation')
        .select('propagation_code')
        .eq('template_id', templateId);
      return this.query('crop_template_propagation', query, {
        cacheKey: cacheManager.generateCacheKey('crop_template_propagation', 'getByTemplate', templateId)
      });
    },

    /**
     * Get propagation codes for multiple templates (bulk fetch)
     * @param {string[]} templateIds - Array of template UUIDs
     * @returns {Promise<{data: Array<{template_id: string, propagation_code: string}>, error}>}
     */
    getByTemplates: async (templateIds) => {
      if (!templateIds || templateIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...templateIds].sort();
      const query = supabase
        .from('crop_template_propagation')
        .select('template_id, propagation_code')
        .in('template_id', sortedIds);
      return this.query('crop_template_propagation', query, {
        cacheKey: cacheManager.generateCacheKey('crop_template_propagation', 'getByTemplates', sortedIds)
      });
    },

    invalidate: () => cacheManager.invalidate('crop_template_propagation')
  };

  /**
   * Crop Phase Types entity getters
   */
  cropPhaseTypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_phase_types')
        .select('*')
        .order('name', { ascending: true });
      return this.query('crop_phase_types', query);
    },

    getById: async (code) => {
      const query = supabase
        .from('crop_phase_types')
        .select('*')
        .eq('code', code)
        .single();
      return this.query('crop_phase_types', query, {
        cacheKey: cacheManager.generateCacheKey('crop_phase_types', 'getById', code)
      });
    },

    getByPropagationType: async (propagationCode) => {
      const query = supabase
        .from('crop_phase_types')
        .select('*')
        .contains('applies_to_crop_propagation_types', [propagationCode])
        .order('typical_duration_days', { ascending: true });
      return this.query('crop_phase_types', query, {
        cacheKey: cacheManager.generateCacheKey('crop_phase_types', 'getByPropagationType', propagationCode)
      });
    },

    invalidate: () => cacheManager.invalidate('crop_phase_types')
  };

  /**
   * Crop Schedules entity getters
   */
  cropSchedules = {
    getAll: async () => {
      const query = supabase
        .from('crop_schedules')
        .select('*')
        .order('created_at', { ascending: false });
      return this.query('crop_schedules', query);
    },

    getById: async (id) => {
      const query = supabase
        .from('crop_schedules')
        .select('*')
        .eq('id', id)
        .single();
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getById', id)
      });
    },

    getByCrop: async (cropId) => {
      const query = supabase
        .from('crop_schedules')
        .select('*')
        .eq('crop_id', cropId)
        .order('created_at', { ascending: false });
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getByCrop', cropId)
      });
    },

    // Active schedule = the schedule whose linked season is still active
    getActiveByCrop: async (cropId) => {
      const query = supabase
        .from('crop_schedules')
        .select('*, season:seasons!inner(state)')
        .eq('crop_id', cropId)
        .eq('seasons.state', 'active')
        .maybeSingle();
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getActiveByCrop', cropId)
      });
    },

    // Archived schedules = schedules whose linked season has ended
    getArchivedByCrop: async (cropId) => {
      const query = supabase
        .from('crop_schedules')
        .select('*, season:seasons!inner(state, ended_at)')
        .eq('crop_id', cropId)
        .eq('seasons.state', 'ended')
        .order('ended_at', { referencedTable: 'seasons', ascending: false });
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getArchivedByCrop', cropId)
      });
    },

    /**
     * Fetch all active schedules with nested crops and phases
     * Single aggregate query replacing N+M parallel queries
     */
    getActiveWithPhasesAndCrops: async () => {
      const query = supabase
        .from('crop_schedules')
        .select(`
          *,
          season:seasons!inner(state),
          crop:crops (
            id, name, count, notes, growth_code,
            archetype_code, organization_id
          ),
          crop_phases (
            id, phase_type_code, est_started_at, est_duration,
            started_at, duration, step, state,
            phase_type:crop_phase_types (code, name, typical_duration_days)
          )
        `)
        .eq('seasons.state', 'active')
        .order('step', { referencedTable: 'crop_phases', ascending: true });
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getActiveWithPhasesAndCrops', null)
      });
    },

    /**
     * Fetch active schedules for specified crop IDs
     * Does NOT include crop join - crop data comes from upstream (useCropData)
     * This preserves all upstream joins (organization, templates, families)
     */
    getActiveByCropIds: async (cropIds) => {
      if (!cropIds || cropIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...cropIds].sort();
      const query = supabase
        .from('crop_schedules')
        .select(`
          *,
          season:seasons!inner(state),
          crop_phases (
            id, phase_type_code, est_started_at, est_duration,
            started_at, duration, step, state,
            phase_type:crop_phase_types(code, name, typical_duration_days)
          )
        `)
        .eq('seasons.state', 'active')
        .in('crop_id', sortedIds)
        .order('step', { referencedTable: 'crop_phases', ascending: true });
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getActiveByCropIds', sortedIds)
      });
    },

    /**
     * Fetch schedules for a specific season + crop IDs
     * Used when viewing historical (ended) seasons to load the correct schedule data.
     */
    getByCropIdsAndSeason: async (cropIds, seasonId) => {
      if (!cropIds || cropIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...cropIds].sort();
      const query = supabase
        .from('crop_schedules')
        .select(`
          *,
          season:seasons!inner(state),
          crop_phases (
            id, phase_type_code, est_started_at, est_duration,
            started_at, duration, step, state,
            phase_type:crop_phase_types(code, name, typical_duration_days)
          )
        `)
        .eq('season_id', seasonId)
        .in('crop_id', sortedIds)
        .order('step', { referencedTable: 'crop_phases', ascending: true });
      return this.query('crop_schedules', query, {
        cacheKey: cacheManager.generateCacheKey('crop_schedules', 'getByCropIdsAndSeason', { sortedIds, seasonId })
      });
    },

    invalidate: () => cacheManager.invalidate('crop_schedules')
  };

  /**
   * Crop Phases entity getters
   */
  cropPhases = {
    getAll: async () => {
      const query = supabase
        .from('crop_phases')
        .select(`
          *,
          phase_type:crop_phase_types(code, name, description, typical_duration_days)
        `)
        .order('step', { ascending: true });
      return this.query('crop_phases', query);
    },

    getById: async (id) => {
      const [scheduleId, step] = id.split('#');
      const query = supabase
        .from('crop_phases')
        .select(`
          *,
          phase_type:crop_phase_types(code, name, description, typical_duration_days)
        `)
        .eq('schedule_id', scheduleId)
        .eq('step', parseInt(step, 10))
        .single();
      return this.query('crop_phases', query, {
        cacheKey: cacheManager.generateCacheKey('crop_phases', 'getById', id)
      });
    },

    getBySchedule: async (scheduleId) => {
      const query = supabase
        .from('crop_phases')
        .select(`
          *,
          phase_type:crop_phase_types(code, name, description, typical_duration_days)
        `)
        .eq('schedule_id', scheduleId)
        .order('step', { ascending: true });
      return this.query('crop_phases', query, {
        cacheKey: cacheManager.generateCacheKey('crop_phases', 'getBySchedule', scheduleId)
      });
    },

    getActiveBySchedule: async (scheduleId) => {
      const query = supabase
        .from('crop_phases')
        .select(`
          *,
          phase_type:crop_phase_types(code, name, description, typical_duration_days)
        `)
        .eq('schedule_id', scheduleId)
        .eq('state', 'active')
        .single();
      return this.query('crop_phases', query, {
        cacheKey: cacheManager.generateCacheKey('crop_phases', 'getActiveBySchedule', scheduleId)
      });
    },

    invalidate: () => cacheManager.invalidate('crop_phases')
  };

  /**
   * Crop Crop propagation types entity getters
   */
  cropPropagationYypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_propagation_types')
        .select('*')
      return this.query('crop_propagation_types', query);
    },
  
    invalidate: () => cacheManager.invalidate('crop_propagation_types')
  };

  /**
   * Crop Crop growth types entity getters
   */
  cropGrowthTypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_growth_types')
        .select('*')
      return this.query('crop_growth_types', query);
    },
  
    invalidate: () => cacheManager.invalidate('crop_growth_types')
  };

  /**
   * Crop Lifecycle Types entity getters
   */
  cropLifecycleTypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_lifecycle_types')
        .select('*')
      return this.query('crop_lifecycle_types', query);
    },
  
    invalidate: () => cacheManager.invalidate('crop_lifecycle_types')
  };

  /**
   * Crop Archetypes entity getters
   */
  cropArchetypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_archetypes')
        .select('*')
      return this.query('crop_archetypes', query);
    },
  
    invalidate: () => cacheManager.invalidate('crop_archetypes')
  };

  /**
   * Crop Family Types entity getters
   */
  cropFamilyTypes = {
    getAll: async () => {
      const query = supabase
        .from('crop_family_types')
        .select('code, name, latin_name, description')
        .order('name')
      return this.query('crop_family_types_all', query);
    },

    invalidate: () => cacheManager.invalidate('crop_family_types_all')
  };

  /**
   * Crop template collection types entity getters
   */
  cropTemplateCollectionTypes = {
    getAll: async () => {
      const query = supabase.from('crop_template_collection_types').select('*').order('code');
      return this.query('crop_template_collection_types', query);
    },

    invalidate: () => cacheManager.invalidate('crop_template_collection_types')
  };

  /**
   * Global Climate Atlas entity getters
   * Provides planetary-scale climate lookups using PostGIS spatial indexing.
   */
  globalClimateAtlas = {
    /**
     * Find the single closest climate hexagon to a specific coordinate.
     * Uses the PostGIS `<->` operator for high-performance "Nearest Neighbor" search.
     */
    getNearest: async (lat, lng) => {
      const query = supabase.rpc('get_nearest_climate_hex', {
        target_lat: lat,
        target_lng: lng
      }).single();

      return this.query('global_climate_atlas', query, {
        cacheKey: cacheManager.generateCacheKey('global_climate_atlas', 'getNearest', { lat, lng })
      });
    },

    /**
     * Find the N closest climate hexagons to a specific coordinate.
     * Default returns 7 hexes (center + surrounding ring) for microclimate selection.
     */
    getNearestMultiple: async (lat, lng, count = 7) => {
      const query = supabase.rpc('get_nearest_climate_hexes', {
        target_lat: lat,
        target_lng: lng,
        hex_count: count
      });

      return this.query('global_climate_atlas', query, {
        cacheKey: cacheManager.generateCacheKey('global_climate_atlas', 'getNearestMultiple', { lat, lng, count })
      });
    },

    /**
     * Get interpolated climate data using Inverse Distance Weighting (IDW).
     * Calculates optimal frost dates and GDD values from N nearest hexagons.
     * Returns both interpolated values AND contributing hex metadata for UI transparency.
     */
    getInterpolated: async (lat, lng, count = 7) => {
      const query = supabase.rpc('interpolate_climate_data', {
        target_lat: lat,
        target_lng: lng,
        hex_count: count
      }).single();

      return this.query('global_climate_atlas', query, {
        cacheKey: cacheManager.generateCacheKey('global_climate_atlas', 'getInterpolated', {
          lat, lng, count
        }),
        ttl: 1000 * 60 * 60 // Long TTL (1 hour) as global climate data is static
      });
    },

    /**
     * Fetch all climate hexagons within a map viewport (Bounding Box).
     * Calls the 'get_climate_by_bbox' Postgres function.
     */
    getInBBox: async (minLat, minLng, maxLat, maxLng) => {
      const query = supabase.rpc('get_climate_by_bbox', {
        min_lat: minLat,
        min_lng: minLng,
        max_lat: maxLat,
        max_lng: maxLng
      });

      return this.query('global_climate_atlas', query, {
        cacheKey: cacheManager.generateCacheKey('global_climate_atlas', 'getInBBox', { 
          minLat, minLng, maxLat, maxLng 
        }),
        ttl: 1000 * 60 * 60 // Long TTL (1 hour) as global climate data is static
      });
    },

    /**
     * Direct lookup by H3 Index string.
     */
    getByH3Index: async (h3Index) => {
      const query = supabase
        .from('global_climate_atlas')
        .select('*')
        .eq('h3_index', h3Index)
        .single();

      return this.query('global_climate_atlas', query, {
        cacheKey: cacheManager.generateCacheKey('global_climate_atlas', 'getByH3Index', h3Index)
      });
    },

    invalidate: () => cacheManager.invalidate('global_climate_atlas')
  };

  /**
   * Tasks entity getters
   */
  tasks = {
    getAll: async () => {
      const query = supabase.from('tasks').select('*, task_types(*), task_states(*)');
      return this.query('tasks', query);
    },

    getById: async (id) => {
      const query = supabase
        .from('tasks')
        .select('*, task_types(*), task_states(*)')
        .eq('id', id)
        .single();
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getById', id)
      });
    },

    getByCrop: async (cropId) => {
      const query = supabase
        .from('tasks')
        .select('*, task_types(*), task_states(*)')
        .eq('crop_id', cropId)
        .order('task_window_start', { ascending: true });
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getByCrop', cropId)
      });
    },

    getByCrops: async (cropIds) => {
      if (!cropIds || cropIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...cropIds].sort();
      const query = supabase
        .from('tasks')
        .select('*, task_type:task_types(code, name, description, target, single_completion)')
        .in('crop_id', sortedIds)
        .order('task_window_start', { ascending: true });
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getByCrops', sortedIds)
      });
    },

    getByOrganization: async (organizationId) => {
      const query = supabase
        .from('tasks')
        .select('*, task_types(*), task_states(*)')
        .eq('organization_id', organizationId)
        .order('task_window_start', { ascending: true });
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getByOrganization', organizationId)
      });
    },

    getByCropSchedule: async (scheduleId) => {
      // Join via crop_id from schedule
      const query = supabase
        .from('tasks')
        .select('*, task_types(*), task_states(*), crop_schedules!inner(id)')
        .eq('crop_schedules.id', scheduleId)
        .order('task_window_start', { ascending: true });
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getByCropSchedule', scheduleId)
      });
    },

    /**
     * Fetch tasks for multiple organizations (including crops)
     * @param {Array<string>} orgIds - Array of organization IDs
     * @returns {Promise<{data, error}>} Combined org-level and crop-level tasks
     */
    getByOrganizations: async (orgIds) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }

      const sortedIds = [...orgIds].sort();
      const cacheKey = cacheManager.generateCacheKey('tasks', 'getByOrganizations', sortedIds);

      const query = (async () => {
        try {
          // Fetch organization-level tasks (crop_id is null)
          const { data: orgTasks, error: orgError } = await supabase
            .from('tasks')
            .select(`
              *,
              task_types(*),
              task_states(*),
              organization:organizations!tasks_organization_id_fkey(id, name, organization_type)
            `)
            .in('organization_id', sortedIds)
            .is('crop_id', null)
            .order('task_window_start', { ascending: true });

          if (orgError) throw orgError;

          // Fetch crop IDs from all organizations
          const { data: crops, error: cropsError } = await supabase
            .from('crops')
            .select('id')
            .in('organization_id', sortedIds);

          if (cropsError) throw cropsError;

          const cropIds = crops?.map(c => c.id) || [];

          // Fetch crop-level tasks (if any crops exist)
          let cropTasks = [];
          if (cropIds.length > 0) {
            const { data, error: cropTasksError } = await supabase
              .from('tasks')
              .select(`
                *,
                task_types(*),
                task_states(*),
                crop:crops!tasks_crop_id_fkey(
                  id,
                  name,
                  organization:organizations(id, name, organization_type)
                )
              `)
              .in('crop_id', cropIds)
              .not('crop_id', 'is', null)
              .order('task_window_start', { ascending: true });

            if (cropTasksError) throw cropTasksError;
            cropTasks = data || [];
          }

          // Merge org tasks and crop tasks
          const allTasks = [...(orgTasks || []), ...cropTasks];

          return { data: allTasks, error: null };
        } catch (err) {
          console.error('[TASKS] Error fetching tasks:', err);
          return { data: null, error: err };
        }
      })();

      return this.query('tasks', query, { cacheKey });
    },

    /**
     * Fetch only org-level tasks (crop_id IS NULL) for multiple organizations
     * @param {Array<string>} orgIds - Array of organization IDs
     * @returns {Promise<{data, error}>} Org-level tasks only
     */
    getOrgTasksByOrganizations: async (orgIds) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...orgIds].sort();
      const query = supabase
        .from('tasks')
        .select(`
          *,
          task_types(*),
          task_states(*),
          organization:organizations!tasks_organization_id_fkey(id, name, organization_type)
        `)
        .in('organization_id', sortedIds)
        .is('crop_id', null)
        .order('task_window_start', { ascending: true });
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getOrgTasksByOrganizations', sortedIds)
      });
    },

    getWithUserByOrganizationsAndSeason: async (orgIds, seasonId) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }
      const sortedIds = [...orgIds].sort();
      const query = supabase.from('tasks')
        .select(`
          id, notes, created_at, updated_at, crop_id, organization_id,
          task_type:task_types(code, name, target),
          created_by:users!tasks_created_by_fkey(id, username, email, avatar_url),
          organization:organizations(id, name),
          crop:crops(id, name)
        `)
        .in('organization_id', sortedIds)
        .eq('season_id', seasonId)
        .not('notes', 'is', null);
      return this.query('tasks', query, {
        cacheKey: cacheManager.generateCacheKey('tasks', 'getWithUserByOrganizationsAndSeason', { sortedIds, seasonId })
      });
    },

    /**
     * Fetch tasks for multiple organizations filtered by season
     * @param {Array<string>} orgIds - Array of organization IDs
     * @param {string} seasonId - Season UUID
     * @returns {Promise<{data, error}>} Combined org-level and crop-level tasks for the season
     */
    getByOrganizationsAndSeason: async (orgIds, seasonId) => {
      if (!orgIds || orgIds.length === 0) {
        return { data: [], error: null };
      }

      const sortedIds = [...orgIds].sort();
      const cacheKey = cacheManager.generateCacheKey('tasks', 'getByOrganizationsAndSeason', { orgIds: sortedIds, seasonId });

      const query = (async () => {
        try {
          // Fetch org-level tasks — include tasks scoped to this season OR with no season
          // (org tasks like plot_winter_prep are not season-scoped)
          const { data: orgTasks, error: orgError } = await supabase
            .from('tasks')
            .select(`
              *,
              task_types(*),
              task_states(*),
              organization:organizations!tasks_organization_id_fkey(id, name, organization_type)
            `)
            .in('organization_id', sortedIds)
            .is('crop_id', null)
            .or(`season_id.eq.${seasonId},season_id.is.null`)
            .order('task_window_start', { ascending: true });

          if (orgError) throw orgError;

          // Fetch crop IDs from all organizations
          const { data: crops, error: cropsError } = await supabase
            .from('crops')
            .select('id')
            .in('organization_id', sortedIds);

          if (cropsError) throw cropsError;

          const cropIds = crops?.map(c => c.id) || [];

          // Fetch crop-level tasks scoped to this season
          let cropTasks = [];
          if (cropIds.length > 0) {
            const { data, error: cropTasksError } = await supabase
              .from('tasks')
              .select(`
                *,
                task_types(*),
                task_states(*),
                crop:crops!tasks_crop_id_fkey(
                  id,
                  name,
                  organization:organizations(id, name, organization_type)
                )
              `)
              .in('crop_id', cropIds)
              .not('crop_id', 'is', null)
              .eq('season_id', seasonId)
              .order('task_window_start', { ascending: true });

            if (cropTasksError) throw cropTasksError;
            cropTasks = data || [];
          }

          return { data: [...(orgTasks || []), ...cropTasks], error: null };
        } catch (err) {
          console.error('[TASKS] Error fetching tasks by season:', err);
          return { data: null, error: err };
        }
      })();

      return this.query('tasks', query, { cacheKey });
    },

    invalidate: () => cacheManager.invalidate('tasks')
  };

  /**
   * Task types entity getters (reference data)
   */
  taskTypes = {
    getAll: async () => {
      const query = supabase.from('task_types').select('*').order('name', { ascending: true });
      return this.query('task_types', query);
    },

    getByCode: async (code) => {
      const query = supabase
        .from('task_types')
        .select('*')
        .eq('code', code)
        .single();
      return this.query('task_types', query, {
        cacheKey: cacheManager.generateCacheKey('task_types', 'getByCode', code)
      });
    },

    getCropTasks: async () => {
      const query = supabase
        .from('task_types')
        .select('*')
        .eq('target', 'crop')
        .order('name', { ascending: true });
      return this.query('task_types', query);
    },

    getPlotTasks: async () => {
      const query = supabase
        .from('task_types')
        .select('*')
        .eq('target', 'plot')
        .order('name', { ascending: true });
      return this.query('task_types', query);
    },

    invalidate: () => cacheManager.invalidate('task_types')
  };

  /**
   * Crop Family Companions M2M table getters
   * Columns: family_code, companion_code (bidirectional)
   */
  cropFamilyCompanions = {
    getByFamilyCodes: async (familyCodes) => {
      if (!familyCodes || familyCodes.length === 0) return { data: [], error: null }
      const sortedCodes = [...familyCodes].sort()
      const codeList = sortedCodes.join(',')
      const query = supabase
        .from('crop_family_companions')
        .select('family_code, companion_code')
        .or(`family_code.in.(${codeList}),companion_code.in.(${codeList})`)
      return this.query('crop_family_companions', query, {
        cacheKey: cacheManager.generateCacheKey('crop_family_companions', 'getByFamilyCodes', sortedCodes)
      })
    },

    invalidate: () => cacheManager.invalidate('crop_family_companions')
  };

  /**
   * Crop Family Incompatibles M2M table getters
   * Columns: family_code, incompatible_code (bidirectional)
   */
  cropFamilyIncompatibles = {
    getByFamilyCodes: async (familyCodes) => {
      if (!familyCodes || familyCodes.length === 0) return { data: [], error: null }
      const sortedCodes = [...familyCodes].sort()
      const codeList = sortedCodes.join(',')
      const query = supabase
        .from('crop_family_incompatibles')
        .select('family_code, incompatible_code')
        .or(`family_code.in.(${codeList}),incompatible_code.in.(${codeList})`)
      return this.query('crop_family_incompatibles', query, {
        cacheKey: cacheManager.generateCacheKey('crop_family_incompatibles', 'getByFamilyCodes', sortedCodes)
      })
    },

    invalidate: () => cacheManager.invalidate('crop_family_incompatibles')
  };

  /**
   * Crop Template Companions M2M table getters
   * Columns: template_id, companion_id (bidirectional)
   */
  cropTemplateCompanions = {
    getByTemplateIds: async (templateIds) => {
      if (!templateIds || templateIds.length === 0) return { data: [], error: null }
      const sortedIds = [...templateIds].sort()
      const idList = sortedIds.join(',')
      const query = supabase
        .from('crop_template_companions')
        .select('template_id, companion_id')
        .or(`template_id.in.(${idList}),companion_id.in.(${idList})`)
      return this.query('crop_template_companions', query, {
        cacheKey: cacheManager.generateCacheKey('crop_template_companions', 'getByTemplateIds', sortedIds)
      })
    },

    invalidate: () => cacheManager.invalidate('crop_template_companions')
  };

  /**
   * Crop Template Incompatibles M2M table getters
   * Columns: template_id, incompatible_id (bidirectional)
   */
  cropTemplateIncompatibles = {
    getByTemplateIds: async (templateIds) => {
      if (!templateIds || templateIds.length === 0) return { data: [], error: null }
      const sortedIds = [...templateIds].sort()
      const idList = sortedIds.join(',')
      const query = supabase
        .from('crop_template_incompatibles')
        .select('template_id, incompatible_id')
        .or(`template_id.in.(${idList}),incompatible_id.in.(${idList})`)
      return this.query('crop_template_incompatibles', query, {
        cacheKey: cacheManager.generateCacheKey('crop_template_incompatibles', 'getByTemplateIds', sortedIds)
      })
    },

    invalidate: () => cacheManager.invalidate('crop_template_incompatibles')
  };

  /**
   * Reports entity getters
   */
  reports = {
    getAll: async () => {
      const query = supabase.from('reports').select('*, report_types(*), reported_by:users(id, username, email)').order('created_at', { ascending: false });
      return this.query('reports', query);
    },

    getById: async (id) => {
      const query = supabase
        .from('reports')
        .select('*, report_types(*), reported_by:users(id, username, email)')
        .eq('id', id)
        .single();
      return this.query('reports', query, {
        cacheKey: cacheManager.generateCacheKey('reports', 'getById', id)
      });
    },

    getByOrganization: async (organizationId) => {
      const query = supabase
        .from('reports')
        .select('*, report_types(*), reported_by:users(id, username, email)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false });
      return this.query('reports', query, {
        cacheKey: cacheManager.generateCacheKey('reports', 'getByOrganization', organizationId)
      });
    },

    getByOrganizationAndCode: async (organizationId, reportCode) => {
      const query = supabase
        .from('reports')
        .select('*, report_types(*), reported_by:users(id, username, email)')
        .eq('organization_id', organizationId)
        .eq('report_code', reportCode)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return this.query('reports', query, {
        cacheKey: cacheManager.generateCacheKey('reports', 'getByOrganizationAndCode', { organizationId, reportCode })
      });
    },

    getByUser: async (userId) => {
      const query = supabase
        .from('reports')
        .select('*, report_types(*), organization:organizations(id, name, organization_type)')
        .eq('reported_by', userId)
        .order('created_at', { ascending: false });
      return this.query('reports', query, {
        cacheKey: cacheManager.generateCacheKey('reports', 'getByUser', userId)
      });
    },

    getByOrganizationAndSeason: async (organizationId, seasonId) => {
      const query = supabase
        .from('reports')
        .select('*, report_types(*), reported_by:users(id, username, email, avatar_url), organization:organizations(id, name)')
        .eq('organization_id', organizationId)
        .eq('season_id', seasonId)
        .order('created_at', { ascending: false });
      return this.query('reports', query, {
        cacheKey: cacheManager.generateCacheKey('reports', 'getByOrganizationAndSeason', { organizationId, seasonId })
      });
    },

    invalidate: () => cacheManager.invalidate('reports')
  };

  /**
   * Report types entity getters (reference data)
   */
  reportTypes = {
    getAll: async () => {
      const query = supabase.from('report_types').select('*').order('name', { ascending: true });
      return this.query('report_types', query);
    },

    getByCode: async (code) => {
      const query = supabase
        .from('report_types')
        .select('*')
        .eq('code', code)
        .single();
      return this.query('report_types', query, {
        cacheKey: cacheManager.generateCacheKey('report_types', 'getByCode', code)
      });
    },

    invalidate: () => cacheManager.invalidate('report_types')
  };

  /**
   * Task states entity getters (reference data)
   */
  taskStates = {
    getAll: async () => {
      const query = supabase.from('task_states').select('*');
      return this.query('task_states', query);
    },

    getByCode: async (code) => {
      const query = supabase
        .from('task_states')
        .select('*')
        .eq('code', code)
        .single();
      return this.query('task_states', query, {
        cacheKey: cacheManager.generateCacheKey('task_states', 'getByCode', code)
      });
    },

    invalidate: () => cacheManager.invalidate('task_states')
  };

  /**
   * Metering entity getters
   */
  metering = {
    // Full byte total across all metered tables — same source as the breakdown RPC.
    // Uses get_user_byte_size (SECURITY DEFINER, recursive org traversal) so the
    // sidebar total always matches the sum of the account page breakdown subjects.
    getUserOrgUsage: async (userId) => {
      const cacheKey = cacheManager.generateCacheKey('metering', 'getUserOrgUsage', userId);
      const queryPromise = (async () => {
        const { data, error } = await supabase.rpc('get_user_byte_size', { p_user_id: userId });
        return { data, error };
      })();
      return this.query('metering', queryPromise, { cacheKey });
    },

    // User storage state: limits, flags, last reconciliation timestamp
    getUserStorage: async (userId) => {
      const query = supabase
        .from('user_storage')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();
      return this.query('user_storage', query, {
        cacheKey: cacheManager.generateCacheKey('user_storage', 'getByUserId', userId)
      });
    },

    // Per-subject byte breakdown (obfuscated grouping for UI display)
    getUserByteBreakdown: async (userId) => {
      const cacheKey = cacheManager.generateCacheKey('metering', 'getUserByteBreakdown', userId);
      const queryPromise = (async () => {
        const { data, error } = await supabase.rpc('get_user_byte_size_by_subject', { p_user_id: userId });
        return { data, error };
      })();
      return this.query('metering', queryPromise, { cacheKey });
    },

    invalidate: () => {
      cacheManager.invalidate('metering');
      cacheManager.invalidate('user_storage');
    }
  };

  /**
   * Billing Tier Types entity getters (reference data — rarely changes)
   */
  billingTierTypes = {
    getAll: async () => {
      const query = supabase.from('billing_tier_types').select('*').order('set_index');
      return this.query('billing_tier_types', query, {
        cacheKey: cacheManager.generateCacheKey('billing_tier_types', 'getAll', null)
      });
    },

    getByCode: async (code) => {
      const query = supabase.from('billing_tier_types').select('*').eq('code', code).single();
      return this.query('billing_tier_types', query, {
        cacheKey: cacheManager.generateCacheKey('billing_tier_types', 'getByCode', code)
      });
    },

    invalidate: () => cacheManager.invalidate('billing_tier_types')
  };

  /**
   * Billing entity getters — billing row, status view, and transaction history.
   * Transactions are INSERT-only from service_role (Edge Functions); clients read-only.
   */
  billing = {
    // Raw billing row: tier code, total credits, recurring settings
    getByUser: async (userId) => {
      const query = supabase.from('billing').select('*').eq('user_id', userId).single();
      return this.query('billing', query, {
        cacheKey: cacheManager.generateCacheKey('billing', 'getByUser', userId)
      });
    },

    // Computed view: joins tier, exposes current_storage_limit_kb and _bytes
    getStatusByUser: async (userId) => {
      const query = supabase.from('user_billing_status').select('*').eq('user_id', userId).single();
      return this.query('user_billing_status', query, {
        cacheKey: cacheManager.generateCacheKey('user_billing_status', 'getByUser', userId)
      });
    },

    // Full transaction history for a user, newest first
    getTransactionsByUser: async (userId) => {
      const query = supabase.from('billing_transactions').select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      return this.query('billing_transactions', query, {
        cacheKey: cacheManager.generateCacheKey('billing_transactions', 'getByUser', userId)
      });
    },

    // Clears all three namespaces touched by this entity group
    invalidate: () => {
      cacheManager.invalidate('billing');
      cacheManager.invalidate('user_billing_status');
      cacheManager.invalidate('billing_transactions');
      cacheManager.invalidate('user_resolved_features');
    }
  };

  /**
   * User Features entity getters (master registry — static reference data)
   */
  userFeatures = {
    getAll: async () => {
      const query = supabase.from('user_features').select('*').order('id');
      return this.query('user_features', query);
    },

    invalidate: () => cacheManager.invalidate('user_features')
  };

  /**
   * Features — resolved per-user feature map.
   * Cascade merge: billing_tier_types tiers applied in set_index order (free→premium→beta),
   * then users.features sparse overrides on top. User keys win on all collisions.
   * Uses two cached queries: billing join (user_resolved_features, 5 min) +
   * all tier types (billing_tier_types, 60 min).
   */
  features = {
    resolveByUser: async (userId) => {
      const billingQuery = supabase
        .from('billing')
        .select('billing_tier_types(set_index), users(features)')
        .eq('user_id', userId)
        .single();

      const { data: billingData, error } = await this.query('user_resolved_features', billingQuery, {
        cacheKey: cacheManager.generateCacheKey('user_resolved_features', 'resolveByUser', userId)
      });

      if (error || !billingData) return { data: null, error };

      const { data: allTiers, error: tierError } = await this.billingTierTypes.getAll();
      if (tierError || !allTiers) return { data: null, error: tierError };

      const extractMap = arr => (arr || []).reduce((acc, e) => {
        const [k, v] = Object.entries(e)[0];
        acc[k] = v;
        return acc;
      }, {});

      const userSetIndex = billingData.billing_tier_types?.set_index ?? 0;
      const tierFeatures = allTiers
        .filter(t => t.set_index <= userSetIndex)
        .reduce((acc, tier) => ({ ...acc, ...extractMap(tier.features) }), {});

      return {
        data: { ...tierFeatures, ...extractMap(billingData.users?.features) },
        error: null
      };
    },

    invalidate: () => cacheManager.invalidate('user_resolved_features')
  };
}


// Export singleton instance
export const supabaseGetter = new SupabaseGetter();

// Export class for testing
export { SupabaseGetter };
